import React, { useEffect, useState } from 'react';
import api from '../api';
import { usePermissions, P, canView } from '../PermissionsContext';

// API serveris (bildes tiek piegādātas no /uploads), lai būvētu logo URL
const SERVER_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000')
  .replace(/\/api\/?$/, '').replace(/\/$/, '');

// Navigācija grupēta pēc domēna — samazina kognitīvo slodzi (17 → 4 grupas).
const navGroups = [
  {
    label: null,
    items: [
      { id: 'dashboard', label: 'Panelis', icon: '▦', canSee: () => true },
    ],
  },
  {
    label: 'Finanses',
    items: [
      { id: 'clients',   label: 'Klienti',      icon: '👥', canSee: has => has(P.CLIENT_VIEW) },
      { id: 'products',  label: 'Pakalpojumi',  icon: '🧾', canSee: has => has(P.PRODUCT_VIEW) },
      { id: 'invoices',  label: 'Rēķini',       icon: '📄', canSee: has => canView(has, P.INVOICE_VIEW_ALL, P.INVOICE_VIEW_OWN) },
      { id: 'recurring', label: 'Atkārtotie',   icon: '🔁', canSee: has => canView(has, P.INVOICE_VIEW_ALL, P.INVOICE_VIEW_OWN) },
      { id: 'bank',      label: 'Banka',        icon: '🏦', canSee: has => has(P.BANK_VIEW) },
      { id: 'suppliers', label: 'Piegādātāji',  icon: '🏢', canSee: has => has(P.SUPPLIER_VIEW) },
      { id: 'expenses',  label: 'Izdevumi',     icon: '💸', canSee: has => canView(has, P.EXPENSE_VIEW_ALL, P.EXPENSE_VIEW_OWN) },
      { id: 'reports',   label: 'Atskaites',    icon: '📊', canSee: has => has(P.REPORTS_VIEW) },
    ],
  },
  {
    label: 'Inventārs',
    items: [
      { id: 'inventory',   label: 'Inventārs',       icon: '📦', canSee: has => has(P.INVENTORY_VIEW) },
      { id: 'warehouses',  label: 'Noliktavas',      icon: '🏭', canSee: has => has(P.WAREHOUSE_MANAGE) },
      { id: 'projects',    label: 'Projekti',        icon: '🗂️', canSee: has => has(P.PROJECT_VIEW) },
      { id: 'mobile-scan', label: 'Skenēt QR',       icon: '📷', canSee: has => has(P.INVENTORY_VIEW) },
      { id: 'stocktake',   label: 'Inventarizācija', icon: '🔎', canSee: has => has(P.STOCKTAKE_MANAGE) },
      { id: 'service',     label: 'Serviss',         icon: '🔧', canSee: has => has(P.SERVICE_MANAGE) },
    ],
  },
  {
    label: 'HR & Aktīvi',
    items: [
      { id: 'assets',  label: 'Pamatlīdzekļi', icon: '🏗️', canSee: has => has(P.ASSETS_VIEW) },
      { id: 'payroll', label: 'Darba samaksa', icon: '💼', canSee: has => has(P.PAYROLL_VIEW) },
    ],
  },
  {
    label: 'Administrācija',
    items: [
      { id: 'permissions', label: 'Tiesības',    icon: '🔐', canSee: has => has(P.PERMISSIONS_MANAGE) },
      { id: 'settings',    label: 'Iestatījumi', icon: '⚙',  canSee: has => has(P.COMPANY_SETTINGS) || has(P.USER_MANAGE) },
      { id: 'api-tokens',  label: 'API tokeni',  icon: '🔑', canSee: () => true },
    ],
  },
];

const Sidebar = ({ page, setPage, user, onLogout }) => {
  const [companies, setCompanies] = useState([]);
  const [current, setCurrent] = useState('');
  const { has, loading: permsLoading } = usePermissions();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/companies/mine');
        setCompanies(res.data || []);
        const storedCompanyId = localStorage.getItem('currentCompanyId');
        if (storedCompanyId) setCurrent(storedCompanyId);
      } catch { /* ignore */ }
    })();
  }, []);

  const switchCompany = async (e) => {
    const companyId = Number(e.target.value);
    if (!companyId) return;
    try {
      const res = await api.post('/companies/switch', { companyId });
      if (res.data.currentCompany) {
        localStorage.setItem('currentCompanyId', String(companyId));
        window.location.reload();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Neizdevās pārslēgt uzņēmumu');
    }
  };

  // Pašreizējā uzņēmuma logo (kvadrāta versija) — nav obligāts.
  const activeCompany = companies.find(c => String(c.id) === String(current));
  const squarePath    = activeCompany?.logo_square_path || activeCompany?.logo_path || null;
  const initials = (activeCompany?.name || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '—';

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="company-brand">
          {squarePath ? (
            <img
              src={`${SERVER_BASE}${squarePath}`}
              alt={activeCompany?.name || ''}
              className="company-logo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="company-logo company-logo-fallback">{initials}</div>
          )}
          <h2>{activeCompany?.name || 'Rēķinu sistēma'}</h2>
        </div>
        <p title={user.email}>{user.email}</p>
        {companies.length > 0 && (
          <select value={current} onChange={switchCompany}
            style={{
              marginTop: 8, width: '100%', padding: '6px 8px',
              background: 'rgba(255,255,255,0.1)', color: 'white',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
              fontSize: 13,
            }}>
            {companies.map(c => (
              <option key={c.id} value={c.id} style={{ color: '#1e293b' }}>{c.name}</option>
            ))}
          </select>
        )}
      </div>
      <nav className="sidebar-nav">
        {navGroups.map((group, gi) => {
          const visible = group.items.filter(item =>
            permsLoading ? item.id === 'dashboard' : item.canSee(has)
          );
          if (visible.length === 0) return null;
          return (
            <div key={gi} className="nav-group">
              {group.label && <div className="nav-group-label">{group.label}</div>}
              {visible.map(item => (
                <button key={item.id} className={`nav-item ${page === item.id ? 'active' : ''}`}
                  onClick={() => setPage(item.id)}>
                  <span className="icon">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        <button className="nav-item" onClick={onLogout} style={{ color: '#f87171' }}>
          <span className="icon">↩</span>
          <span>Iziet</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
